import type {
  CategoriaMetaPlantilla,
  CategoriaPlantilla,
  EventoPlantilla,
  TipoEncabezadoPlantilla,
} from '@/types/domain';
import type {
  CampoTemplate,
  CreateTemplateRequest,
  EventoTemplateDef,
  PlantillaParametro,
  PreviewTemplateRequest,
  SeedTemplatesRequest,
  SendTestRequest,
  UpdateTemplateRequest,
  WhatsappTemplate,
} from '@/types/templates';
import { sortParametros, variablesDelCuerpo } from './templates-format';

/**
 * Formulario de una plantilla: estado de la pantalla, conversión al contrato y valores de las
 * previsualizaciones.
 *
 * Igual que en Campañas y Órdenes, la conversión vive separada de los componentes para poder probarla
 * sin navegador (`smoke-templates.tsx`). Cinco detalles del contrato mandan acá:
 *  - una plantilla es la unión de evento + idioma, así que al CREAR el evento no se puede cambiar
 *    (el `unique` es `[tenantId, evento, idioma]` y el backend responde 409 si ya existe);
 *  - el `pie` vacío en el alta no viaja: el backend pone el sugerido del catálogo;
 *  - al EDITAR una plantilla que ya salió de `BORRADOR` solo se pueden mandar `parametros` (el resto lo
 *    rechaza el backend con 400 porque vive en Meta), así que hay un payload por caso;
 *  - `parametros` se alinea con las variables `{{n}}` del cuerpo (`buildParametros`): el backend exige
 *    que cubran EXACTAMENTE esas posiciones y que usen campos del catálogo del evento;
 *  - `headerImageUrl` es obligatorio al mandar una prueba de una plantilla con encabezado de IMAGEN.
 */

/** Estado del formulario de alta/edición (todo texto: los inputs pueden estar vacíos). */
export interface TemplateFormState {
  evento: EventoPlantilla;
  categoria: CategoriaPlantilla;
  categoriaMeta: CategoriaMetaPlantilla;
  /** Nombre en el WABA (minúsculas, números y guiones bajos). */
  nombreMeta: string;
  idioma: string;
  encabezadoTipo: TipoEncabezadoPlantilla;
  encabezadoTexto: string;
  /** Cuerpo con los `{{n}}`. */
  cuerpo: string;
  pie: string;
  /** Mapeo posición → campo, ya ordenado por posición. */
  parametros: PlantillaParametro[];
  /** Solo en el alta: mandarla a revisión de Meta al guardar. */
  enviarAMeta: boolean;
}

/** Estado del diálogo de prueba (`/prueba`): destinatario + valores + imagen del encabezado. */
export interface SendTestFormState {
  /** Número destino con código de país, sin `+` (ej. `573001234567`). */
  to: string;
  /** Valores por clave de campo; vacío = el backend usa el `ejemplo` del catálogo. */
  valores: Record<string, string>;
  /** Solo para plantillas con `encabezadoTipo === 'IMAGEN'`. */
  headerImageUrl: string;
  /** Opcional: guarda el mensaje saliente en el historial de esa conversación. */
  conversationId: string;
}

/** Campos que el backend puede reportar en `details.fieldErrors` al crear/editar (para repartirlos). */
export const TEMPLATE_FORM_FIELDS = [
  'evento',
  'nombreMeta',
  'idioma',
  'categoria',
  'categoriaMeta',
  'encabezadoTipo',
  'encabezadoTexto',
  'cuerpo',
  'pie',
  'parametros',
] as const;

/** Campos del diálogo de prueba. */
export const SEND_TEST_FORM_FIELDS = ['to', 'headerImageUrl', 'conversationId'] as const;

/**
 * Formulario de un evento del catálogo: los textos y el mapeo salen del `*Sugerido` del backend, que
 * es justo lo que siembra `POST .../recomendadas` (así «crear a mano» y «sembrar» dan lo mismo).
 */
export function emptyTemplateForm(
  def: EventoTemplateDef,
  idiomaPorDefecto = 'es',
): TemplateFormState {
  return {
    evento: def.evento,
    categoria: def.categoria,
    categoriaMeta: def.categoriaMeta,
    nombreMeta: def.nombreSugerido,
    idioma: idiomaPorDefecto,
    encabezadoTipo: def.encabezadoTipo,
    encabezadoTexto: def.encabezadoTexto ?? '',
    cuerpo: def.cuerpoSugerido,
    pie: def.pieSugerido ?? '',
    parametros: sortParametros(def.parametrosSugeridos.map((parametro) => ({ ...parametro }))),
    enviarAMeta: false,
  };
}

/** Plantilla de la API → estado del formulario (`def` = catálogo de su evento). */
export function templateFormStateFrom(
  plantilla: WhatsappTemplate,
  def: EventoTemplateDef,
): TemplateFormState {
  return {
    evento: plantilla.evento,
    categoria: plantilla.categoria,
    categoriaMeta: plantilla.categoriaMeta,
    nombreMeta: plantilla.nombreMeta,
    idioma: plantilla.idioma,
    encabezadoTipo: plantilla.encabezadoTipo,
    encabezadoTexto: plantilla.encabezadoTexto ?? '',
    cuerpo: plantilla.cuerpo,
    pie: plantilla.pie ?? '',
    parametros: buildParametros(plantilla.cuerpo, plantilla.parametros, def),
    enviarAMeta: false,
  };
}

/**
 * Alinea el mapeo con las variables que REALMENTE tiene el cuerpo.
 *
 * El cuerpo es editable, así que al agregar `{{3}}` falta una fila y al borrarlo sobra: acá se
 * conserva el campo ya elegido para cada posición que sigue existiendo y se completa lo nuevo con el
 * `parametrosSugeridos` del catálogo (y, si el evento no sugiere nada para esa posición, con su primer
 * campo). Deja el mapeo listo para guardar: el backend rechaza cualquier desajuste.
 */
export function buildParametros(
  cuerpo: string,
  parametros: PlantillaParametro[],
  def: EventoTemplateDef,
): PlantillaParametro[] {
  const porDefecto = def.campos[0]?.clave ?? '';

  return variablesDelCuerpo(cuerpo).map((posicion) => {
    const actual = parametros.find((parametro) => parametro.posicion === posicion);
    const sugerido = def.parametrosSugeridos.find((parametro) => parametro.posicion === posicion);

    return { posicion, campo: actual?.campo ?? sugerido?.campo ?? porDefecto };
  });
}

/**
 * Formulario → body del `POST`.
 *
 * `encabezadoTexto` solo viaja con encabezado de TEXTO (el backend lo ignora en los otros casos pero lo
 * guarda en `null`, así que mandarlo sería ruido) y el `pie` vacío no viaja: el backend pone el sugerido
 * del catálogo.
 */
export function createTemplatePayload(form: TemplateFormState): CreateTemplateRequest {
  const pie = form.pie.trim();

  const payload: CreateTemplateRequest = {
    evento: form.evento,
    categoria: form.categoria,
    categoriaMeta: form.categoriaMeta,
    nombreMeta: form.nombreMeta.trim(),
    idioma: form.idioma.trim(),
    encabezadoTipo: form.encabezadoTipo,
    cuerpo: form.cuerpo.trim(),
    parametros: sortParametros(form.parametros),
    enviarAMeta: form.enviarAMeta,
  };

  if (form.encabezadoTipo === 'TEXTO') payload.encabezadoTexto = form.encabezadoTexto.trim();
  if (pie.length > 0) payload.pie = pie;

  return payload;
}

/**
 * Formulario → body del `PATCH` cuando la plantilla sigue en `BORRADOR` (se edita la fila completa).
 *
 * `encabezadoTexto` y `pie` van como `null` cuando no aplican: el contrato los acepta así (son los dos
 * campos que se pueden vaciar) y con `undefined` significarían «no toques esto».
 */
export function updateTemplatePayload(form: TemplateFormState): UpdateTemplateRequest {
  const pie = form.pie.trim();

  return {
    nombreMeta: form.nombreMeta.trim(),
    idioma: form.idioma.trim(),
    categoria: form.categoria,
    categoriaMeta: form.categoriaMeta,
    encabezadoTipo: form.encabezadoTipo,
    encabezadoTexto: form.encabezadoTipo === 'TEXTO' ? form.encabezadoTexto.trim() : null,
    cuerpo: form.cuerpo.trim(),
    pie: pie.length > 0 ? pie : null,
    parametros: sortParametros(form.parametros),
  };
}

/**
 * `PATCH` de una plantilla que YA está en Meta: solo el mapeo local.
 *
 * Los campos que viven en Meta (nombre, idioma, cuerpo, encabezado…) los rechaza el backend con 400 una
 * vez que la plantilla salió de `BORRADOR`; el dashboard solo puede tocar `parametros` y `activo`.
 */
export function updateParametrosPayload(form: TemplateFormState): UpdateTemplateRequest {
  return { parametros: sortParametros(form.parametros) };
}

/** `PATCH` con un solo campo booleano: el switch de la tabla, sin abrir ningún diálogo. */
export function activoPayload(activo: boolean): UpdateTemplateRequest {
  return { activo };
}

/** Body de `POST .../preview`: solo los valores escritos (el backend completa el resto con ejemplos). */
export function previewPayload(valores: Record<string, string>): PreviewTemplateRequest {
  return { valores: cleanValores(valores) };
}

/** Formulario de prueba vacío (los valores se prellenan con los ejemplos del catálogo desde la pantalla). */
export function emptySendTestForm(valores: Record<string, string> = {}): SendTestFormState {
  return { to: '', valores, headerImageUrl: '', conversationId: '' };
}

/**
 * Formulario de prueba → body del `POST .../prueba`.
 *
 * `headerImageUrl` y `conversationId` solo viajan si tienen algo: el primero es obligatorio para las
 * plantillas de encabezado IMAGEN (el backend responde 400 si falta) y el segundo es opcional.
 */
export function sendTestPayload(form: SendTestFormState): SendTestRequest {
  const headerImageUrl = form.headerImageUrl.trim();
  const conversationId = form.conversationId.trim();

  const payload: SendTestRequest = {
    to: form.to.trim(),
    valores: cleanValores(form.valores),
  };

  if (headerImageUrl.length > 0) payload.headerImageUrl = headerImageUrl;
  if (conversationId.length > 0) payload.conversationId = conversationId;

  return payload;
}

/** Body de `POST .../recomendadas`. */
export function seedPayload(idioma: string, enviarAMeta: boolean): SeedTemplatesRequest {
  const payload: SeedTemplatesRequest = { enviarAMeta };
  const limpio = idioma.trim();
  if (limpio.length > 0) payload.idioma = limpio;

  return payload;
}

/** Valores de ejemplo del catálogo, por clave de campo (lo que se precarga al previsualizar/probar). */
export function valoresEjemplo(campos: CampoTemplate[]): Record<string, string> {
  const valores: Record<string, string> = {};
  for (const campo of campos) valores[campo.clave] = campo.ejemplo;

  return valores;
}

/** Quita los valores vacíos y recorta los demás (el backend rechaza claves de más de 500 caracteres). */
export function cleanValores(valores: Record<string, string>): Record<string, string> {
  const limpios: Record<string, string> = {};

  for (const [clave, valor] of Object.entries(valores)) {
    const texto = valor.trim();
    if (texto.length > 0) limpios[clave] = texto;
  }

  return limpios;
}

